import { useCallback, useEffect, useId, useState } from "react";

// Keyboard behaviour for the app's three custom listboxes — FieldSelect,
// FilterSelect and MultiSelect. All three are a <button>/<input> plus a popover
// rather than a native <select>, so none of the arrow-key handling a real
// control gives you for free was there: a keyboard user could open a menu and
// then have no way to move inside it. Written once here because the three
// differ only in markup, and a third copy of this switch is how they drift.
//
// The caller owns `open`, the option list and what selecting one does. This
// owns the active row, the keys that move it, and the ids that let a screen
// reader follow along.
//
// The caller passes `triggerRef` (what focus returns to) and `listRef` (the
// element the options live in) because both are its own markup.
//
// `onSelect(index)` does NOT close the menu — that is the caller's call, since
// a multi-select stays open across picks while a single-select does not.
const isTypingTarget = (el) =>
  el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA");

export default function useListboxKeys({
  open,
  setOpen,
  count,
  onSelect,
  selectedIndex = -1,
  triggerRef,
  listRef,
}) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const listboxId = useId();
  const optionId = useCallback((i) => `${listboxId}-opt-${i}`, [listboxId]);

  // Opening lands the cursor on the current choice so ArrowDown steps to the
  // NEXT option instead of restarting at the top of a 2500-row roster.
  useEffect(() => {
    setActiveIndex(open ? Math.max(selectedIndex, 0) : -1);
  }, [open, selectedIndex]);

  // A narrowing search can strand the cursor past the end of the list.
  useEffect(() => {
    if (!open) return;
    setActiveIndex((i) => (count === 0 ? -1 : Math.min(Math.max(i, 0), count - 1)));
  }, [count, open]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    listRef.current
      ?.querySelector(`[data-lb-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex, listRef]);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, [setOpen, triggerRef]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        if (!open) return;
        // These menus open inside modals whose own Escape handler listens on
        // document. React dispatches from the root container, so stopping the
        // synthetic event here also stops the native bubble — one press closes
        // the menu, not the dialog behind it.
        e.preventDefault();
        e.stopPropagation();
        close();
        return;
      }
      if (e.key === "Tab") {
        if (open) setOpen(false);
        return;
      }
      const moveTo = (next) => {
        e.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        if (count > 0) setActiveIndex(next);
      };
      switch (e.key) {
        case "ArrowDown":
          moveTo((activeIndex < 0 ? -1 : activeIndex) + 1 >= count ? 0 : activeIndex + 1);
          return;
        case "ArrowUp":
          moveTo(activeIndex <= 0 ? count - 1 : activeIndex - 1);
          return;
        case "Home":
          // In a search box these belong to the caret, not the list.
          if (!isTypingTarget(e.target)) moveTo(0);
          return;
        case "End":
          if (!isTypingTarget(e.target)) moveTo(count - 1);
          return;
        case "Enter":
          if (!open) {
            e.preventDefault();
            setOpen(true);
            return;
          }
          if (activeIndex >= 0 && activeIndex < count) {
            e.preventDefault();
            onSelect(activeIndex);
          }
          return;
        default:
      }
    },
    [open, setOpen, count, activeIndex, onSelect, close],
  );

  return {
    activeIndex,
    setActiveIndex,
    onKeyDown,
    listboxId,
    optionId,
    activeDescendant:
      open && activeIndex >= 0 ? optionId(activeIndex) : undefined,
  };
}
